import { useMutation, useQueryClient } from "@tanstack/react-query";
import { resolveScopedVaultApi } from "../../../api/vault.api";
import { useAppDispatch, useAppStore } from "../../../redux/hooks";
import { Requirement, Folder } from "../../../redux/interfaces/folder.types";
import {
  setActiveRequirement,
  setActiveVault,
} from "../../../redux/slices/vaultSlice";
import { setActiveRequirement as setAdminActiveRequirement } from "../../../redux/slices/requirementsSlice";
import { setActiveFolder } from "../../../redux/slices/folderSlice";
import { vaultKeys } from "../queryKeys";
import {
  applyFileDeletionResponse,
  replaceRequirementInFolder,
} from "../../../utils/vault/fileDeletion";

interface RemoveFileContext {
  requirementId: string;
  vaultId: string | null;
  requirementSnapshot: Requirement;
}

function findRequirementInVault(
  vault: Folder,
  requirementId: string,
): Requirement | null {
  for (const subfolder of vault.subfolders ?? []) {
    const requirement = subfolder.requirements?.find(
      (currentRequirement) => currentRequirement.id === requirementId,
    );

    if (requirement) {
      return requirement;
    }
  }

  return null;
}

export function useRemoveFile(folderId: string | undefined) {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const scopedVaultApi = resolveScopedVaultApi(
    typeof window !== "undefined" ? window.location.pathname : "",
  );

  return useMutation({
    mutationFn: (fileId: string) => scopedVaultApi.removeFile(fileId),
    onMutate: (): RemoveFileContext | undefined => {
      const {
        vault: { activeRequirement, activeVault },
      } = store.getState();

      if (!activeRequirement) return undefined;

      return {
        requirementId: activeRequirement.id,
        vaultId: activeVault?.id ?? null,
        requirementSnapshot: activeRequirement,
      };
    },
    onSuccess: async (data, fileId, context) => {
      if (!context) {
        await queryClient.invalidateQueries({
          queryKey: vaultKeys.folder(folderId ?? ""),
        });
        return;
      }

      const state = store.getState();
      const currentVault =
        state.vault.activeVault?.id === context.vaultId
          ? state.vault.activeVault
          : null;
      const requirementFromVault = currentVault
        ? findRequirementInVault(currentVault, context.requirementId)
        : null;
      const updatedRequirement = applyFileDeletionResponse(
        requirementFromVault ?? context.requirementSnapshot,
        data,
        fileId,
      );

      if (state.vault.activeRequirement?.id === context.requirementId) {
        dispatch(setActiveRequirement(updatedRequirement));
      }

      if (state.requirements.activeRequirement?.id === context.requirementId) {
        dispatch(setAdminActiveRequirement(updatedRequirement));
      }

      if (currentVault) {
        dispatch(
          setActiveVault(
            replaceRequirementInFolder(currentVault, updatedRequirement),
          ),
        );
      }

      const currentAdminFolder = state.folder.activeFolder;
      if (currentAdminFolder) {
        dispatch(
          setActiveFolder(
            replaceRequirementInFolder(currentAdminFolder, updatedRequirement),
          ),
        );
      }

      await queryClient.invalidateQueries({
        queryKey: vaultKeys.folder(folderId ?? ""),
      });
    },
  });
}
